import { LiveData, useLiveData } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Observable } from 'rxjs';

import {
  getWorkspaceSyncDisplayProgress,
  getWorkspaceSyncRawProgress,
} from '../../../components/workspace-selector/workspace-card/sync-progress';
import type { Workspace } from '../entities/workspace';

interface SyncEngineState {
  total: number;
  syncing: number;
}

export interface WorkspaceEntryLoadState {
  isLoading: boolean;
  progress: number | undefined;
  totalDocs: number | undefined;
  loadedDocs: number | undefined;
  dismiss: () => void;
}

export function useWorkspaceEntryLoad(
  workspace: Workspace | null,
  ready: boolean,
  options: { disabled?: boolean } = {}
): WorkspaceEntryLoadState {
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const displayedProgressRef = useRef<number | undefined>(undefined);
  const workspaceIdRef = useRef<string | null>(null);

  const engineState = useLiveData(
    useMemo(() => {
      return workspace
        ? LiveData.from<SyncEngineState | null>(
            workspace.engine.doc.state$ as Observable<SyncEngineState>,
            null
          )
        : null;
    }, [workspace])
  );

  const workspaceId = workspace?.id ?? null;
  const isLocal = !workspace || workspace.flavour === 'local';
  const pending = !ready && !isLocal && !dismissed && !options.disabled;

  useEffect(() => {
    setDismissed(false);
    setVisible(false);
    displayedProgressRef.current = undefined;
    workspaceIdRef.current = workspaceId;
  }, [workspaceId]);

  useEffect(() => {
    if (!pending) {
      setVisible(false);
      return;
    }

    const timer = window.setTimeout(() => setVisible(true), 600);
    return () => window.clearTimeout(timer);
  }, [pending]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setVisible(false);
  }, []);

  if (workspaceIdRef.current !== workspaceId) {
    displayedProgressRef.current = undefined;
    workspaceIdRef.current = workspaceId;
  }

  const hasPendingSync = (engineState?.syncing ?? 0) > 0;
  const rawProgress = engineState
    ? getWorkspaceSyncRawProgress(engineState)
    : undefined;
  const progress = getWorkspaceSyncDisplayProgress(
    displayedProgressRef.current,
    rawProgress,
    hasPendingSync
  );
  displayedProgressRef.current = progress;

  const totalDocs =
    engineState && engineState.total > 0 ? engineState.total : undefined;
  const loadedDocs =
    totalDocs === undefined
      ? undefined
      : totalDocs - Math.min(Math.max(engineState?.syncing ?? 0, 0), totalDocs);

  return {
    isLoading: visible && pending,
    progress,
    totalDocs,
    loadedDocs,
    dismiss,
  };
}
